/**
 * Team / membership facade. In mock mode it uses the in-memory store; otherwise
 * it delegates to the Better-Auth organization plugin client. The real-path
 * calls are loosely typed (`as any`) because the exact org-plugin response
 * shapes are validated against the live backend, not at build time.
 */
import {
  MOCK_ENABLED,
  MOCK_CURRENT_USER_ID,
  MOCK_SESSION_KEY,
  mockCancelInvite,
  mockCanCreateOrg,
  mockDeleteOrg,
  mockInvite,
  mockLeaveOrg,
  mockListInvitations,
  mockListMembers,
  mockOwnedOrgsBlockingDeletion,
  mockPurgeUserOrgs,
  mockRemoveMember,
  mockTransferOwnership,
  mockUpdateRole,
  mockUpdateUserName,
  type MemberRole,
} from "./mock.ts";
import { authClient } from "./auth-client.ts";

export type { MemberRole };

export interface Member {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: MemberRole;
  createdAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
}

export interface TeamData {
  members: Member[];
  invitations: Invitation[];
}

/** The current signed-in user's id (used to label "You" / guard self-actions). */
export function currentUserId(): string | undefined {
  if (MOCK_ENABLED) return MOCK_CURRENT_USER_ID;
  return undefined;
}

export async function listTeam(orgId: string): Promise<TeamData> {
  if (MOCK_ENABLED) {
    return {
      members: mockListMembers(orgId).map((m) => ({ ...m })),
      invitations: mockListInvitations(orgId).map((i) => ({ ...i })),
    };
  }

  const org = authClient.organization as any;
  const full = await org.getFullOrganization({ query: { organizationId: orgId } });
  // Better Auth's client RESOLVES with `{ data: null, error }` on an HTTP
  // failure rather than rejecting, so an ignored `error` reads as "this org has
  // no members" — an empty team where the caller should show its retry state.
  if (full?.error) {
    throw new Error(full.error.message || full.error.statusText || "Failed to load team");
  }
  const data = full?.data ?? {};
  const members: Member[] = (data.members ?? []).map((m: any) => ({
    id: m.id,
    userId: m.userId ?? m.user?.id ?? "",
    name: m.user?.name ?? m.user?.email ?? "—",
    email: m.user?.email ?? "—",
    role: m.role as MemberRole,
    createdAt: m.createdAt ?? new Date().toISOString(),
  }));
  const invitations: Invitation[] = (data.invitations ?? [])
    .filter((i: any) => i.status === "pending")
    .map((i: any) => ({ id: i.id, email: i.email, role: i.role, status: i.status, expiresAt: i.expiresAt }));
  return { members, invitations };
}

export async function inviteMember(orgId: string, email: string, role: MemberRole): Promise<void> {
  if (MOCK_ENABLED) return void mockInvite(orgId, email, role);
  await (authClient.organization as any).inviteMember({ email, role, organizationId: orgId });
}

export async function updateMemberRole(orgId: string, memberId: string, role: MemberRole): Promise<void> {
  if (MOCK_ENABLED) return void mockUpdateRole(orgId, memberId, role);
  await (authClient.organization as any).updateMemberRole({ memberId, role, organizationId: orgId });
}

export async function removeMember(orgId: string, memberId: string): Promise<void> {
  if (MOCK_ENABLED) return void mockRemoveMember(orgId, memberId);
  await (authClient.organization as any).removeMember({ memberIdOrEmail: memberId, organizationId: orgId });
}

export async function cancelInvitation(orgId: string, invitationId: string): Promise<void> {
  if (MOCK_ENABLED) return void mockCancelInvite(orgId, invitationId);
  await (authClient.organization as any).cancelInvitation({ invitationId });
}

/** Transfer ownership to another member (single owner: previous owner -> admin). */
export async function transferOwnership(orgId: string, newOwnerMemberId: string): Promise<void> {
  if (MOCK_ENABLED) return void mockTransferOwnership(orgId, newOwnerMemberId);
  await (authClient.organization as any).updateMemberRole({ memberId: newOwnerMemberId, role: "owner", organizationId: orgId });
}

export async function deleteOrganization(orgId: string): Promise<void> {
  if (MOCK_ENABLED) return void mockDeleteOrg(orgId);
  await (authClient.organization as any).delete({ organizationId: orgId });
}

export async function leaveOrganization(orgId: string): Promise<void> {
  if (MOCK_ENABLED) return void mockLeaveOrg(orgId);
  await (authClient.organization as any).leave({ organizationId: orgId });
}

/** Proactive create-eligibility check (mock). Real mode enforces server-side. */
export function canCreateOrg(): boolean {
  if (MOCK_ENABLED) return mockCanCreateOrg();
  return true;
}

export interface BlockingOrg {
  id: string;
  name: string;
  members: number;
}

/** Owned orgs (with other members) that must be transferred or deleted before
 *  the account can be deleted (block-until-resolved). */
export function ownedOrgsBlockingDeletion(): BlockingOrg[] {
  if (MOCK_ENABLED) return mockOwnedOrgsBlockingDeletion();
  return [];
}

export async function updateProfileName(name: string): Promise<void> {
  if (MOCK_ENABLED) return void mockUpdateUserName(name);
  await (authClient as any).updateUser({ name });
}

/** Delete the account. Sole-member owned orgs are removed; the user leaves the
 *  rest. Caller is responsible for navigating to /login afterward. */
export async function deleteAccount(): Promise<void> {
  if (MOCK_ENABLED) {
    mockPurgeUserOrgs();
    try { window.localStorage.removeItem(MOCK_SESSION_KEY); } catch { /* ignore */ }
    return;
  }
  await (authClient as any).deleteUser({});
}
